import React, { useState } from "react";
import { Box, Stack, Typography, IconButton, Tooltip, CircularProgress, Alert, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import RichEditor from "../../../components/rich-editor";
import ConfirmationModal from "../../../components/confirmation-modal";
import { NormalButton, InvertedButton } from "../../../components/ui/CommonReusables";
import CutoffTag from "./CutoffTag";

const C = {
	navy: "#1A3F7A",
	blue: "#3A7DC9",
	blueFaint: "#EBF5FF",
	border: "#D6E6F7",
	textSub: "#6B7FA3",
	danger: "#C62828",
};

const formatDate = (iso) => (iso ? dayjs(iso).format("MMM D, YYYY h:mm A") : "");

// The editor emits "<p></p>" when empty; judge emptiness on the visible text, as the server does.
const isBlank = (html) => {
	const div = document.createElement("div");
	div.innerHTML = html || "";
	return (div.textContent || "").replace(/[\s ]+/g, "") === "";
};

function NoteComposer({ initialValue = "", onSave, onCancel, saveLabel }) {
	const [draft, setDraft] = useState(initialValue);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");

	const handleSave = async () => {
		setSaving(true);
		setError("");
		try {
			await onSave(draft);
		} catch (e) {
			setError(e.message);
			setSaving(false);
		}
	};

	return (
		<Box sx={{ mb: 1.5 }}>
			<RichEditor value={draft} onChange={setDraft} placeholder="Write a note about this client..." />
			{error && (
				<Alert severity="error" sx={{ mt: 1, py: 0 }}>
					{error}
				</Alert>
			)}
			<Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
				<Box>
					<InvertedButton size="small" variant="outlined" onClick={onCancel} disabled={saving}>
						Cancel
					</InvertedButton>
				</Box>
				<NormalButton size="small" onClick={handleSave} disabled={saving || isBlank(draft)} loading={saving}>
					{saveLabel}
				</NormalButton>
			</Stack>
		</Box>
	);
}

function NoteCard({ note, isEditing, onEdit, onCancelEdit, onSaveEdit, onDelete }) {
	const edited = note.updated_at && note.updated_at !== note.created_at;
	const initial = (note.author?.name || "?").trim().charAt(0).toUpperCase();

	return (
		<Box sx={{ border: `1px solid ${C.border}`, borderRadius: 1.5, p: 1.25, mb: 1.25, backgroundColor: "#fff" }}>
			<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
					<Box
						sx={{
							width: 28,
							height: 28,
							borderRadius: "50%",
							backgroundColor: note.author?.is_admin ? C.navy : C.blue,
							color: "#fff",
							fontSize: 12,
							fontWeight: 700,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						{initial}
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
							<Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.navy, lineHeight: 1.3 }}>
								{note.author?.name}
								{note.author?.is_self ? " (You)" : ""}
							</Typography>
							{note.author?.is_admin && (
								<Box sx={{ backgroundColor: C.blueFaint, color: C.navy, borderRadius: 99, px: 0.75, fontSize: 10, fontWeight: 700 }}>
									Admin
								</Box>
							)}
						</Stack>
						<Typography sx={{ fontSize: 11, color: C.textSub, lineHeight: 1.3 }}>
							{formatDate(note.created_at)}
							{edited && (
								<Tooltip title={`Edited${note.updated_by ? ` by ${note.updated_by}` : ""} on ${formatDate(note.updated_at)}`} arrow>
									<Box component="span" sx={{ fontStyle: "italic", cursor: "default" }}>
										{" "}
										· edited
									</Box>
								</Tooltip>
							)}
							<CutoffTag period={note.period} />
						</Typography>
					</Box>
				</Stack>

				{!isEditing && (note.can_edit || note.can_delete) && (
					<Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
						{note.can_edit && (
							<Tooltip title="Edit note" arrow>
								<IconButton size="small" onClick={onEdit} sx={{ backgroundColor: "transparent !important", color: C.textSub }}>
									<EditOutlinedIcon sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						)}
						{note.can_delete && (
							<Tooltip title="Delete note" arrow>
								<IconButton size="small" onClick={onDelete} sx={{ backgroundColor: "transparent !important", color: C.danger }}>
									<DeleteOutlineIcon sx={{ fontSize: 16 }} />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				)}
			</Stack>

			<Box sx={{ mt: 1 }}>
				{isEditing ? (
					<NoteComposer initialValue={note.note} onSave={onSaveEdit} onCancel={onCancelEdit} saveLabel="Save changes" />
				) : (
					<Box sx={{ "& .ProseMirror": { fontSize: "0.85rem !important" } }}>
						<RichEditor value={note.note} readOnly />
					</Box>
				)}
			</Box>
		</Box>
	);
}

/**
 * Notes for one BoB row. The owning agent and admins can add notes; each note can be edited
 * or deleted by its author or an admin — the server decides, and says so per note.
 *
 * The notes state comes from the panel (useBoBNotes) rather than being fetched here, so the
 * section header can show the count while this section is still collapsed.
 */
export default function ClientNotes({ clientKey, notes, loading, error, createNote, updateNote, deleteNote }) {
	const [composing, setComposing] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [pendingDelete, setPendingDelete] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const [actionError, setActionError] = useState("");

	if (!clientKey) {
		return (
			<Typography sx={{ fontSize: 12, color: C.textSub, fontStyle: "italic", textAlign: "center", py: 2 }}>
				Notes aren't available for this record.
			</Typography>
		);
	}

	const handleCreate = async (html) => {
		await createNote(html);
		setComposing(false);
	};

	const handleUpdate = async (id, html) => {
		await updateNote(id, html);
		setEditingId(null);
	};

	const handleConfirmDelete = async () => {
		setDeleting(true);
		setActionError("");
		try {
			await deleteNote(pendingDelete.id);
			setPendingDelete(null);
		} catch (e) {
			setActionError(e.message);
			setPendingDelete(null);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<Box>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
				<Typography sx={{ fontSize: 11, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5 }}>
					{notes.length} {notes.length === 1 ? "note" : "notes"}
				</Typography>
				{!composing && (
					<Button
						size="small"
						startIcon={<AddIcon sx={{ fontSize: 16 }} />}
						onClick={() => {
							setComposing(true);
							setEditingId(null);
						}}
						sx={{
							textTransform: "none",
							fontWeight: 700,
							fontSize: 12,
							color: `${C.blue} !important`,
							backgroundColor: `${C.blueFaint} !important`,
							"&:hover": { backgroundColor: `${C.border} !important` },
						}}
					>
						Add note
					</Button>
				)}
			</Stack>

			{composing && <NoteComposer onSave={handleCreate} onCancel={() => setComposing(false)} saveLabel="Add note" />}

			{(error || actionError) && (
				<Alert severity="error" sx={{ mb: 1.25, py: 0 }} onClose={actionError ? () => setActionError("") : undefined}>
					{actionError || error}
				</Alert>
			)}

			{loading && notes.length === 0 ? (
				<Box sx={{ py: 2, textAlign: "center" }}>
					<CircularProgress size={20} />
				</Box>
			) : notes.length === 0 && !composing && !error ? (
				<Typography sx={{ fontSize: 12, color: C.textSub, fontStyle: "italic", textAlign: "center", py: 2 }}>
					No notes attached to this client.
				</Typography>
			) : (
				notes.map((note) => (
					<NoteCard
						key={note.id}
						note={note}
						isEditing={editingId === note.id}
						onEdit={() => {
							setEditingId(note.id);
							setComposing(false);
						}}
						onCancelEdit={() => setEditingId(null)}
						onSaveEdit={(html) => handleUpdate(note.id, html)}
						onDelete={() => setPendingDelete(note)}
					/>
				))
			)}

			<ConfirmationModal
				open={!!pendingDelete}
				onClose={() => !deleting && setPendingDelete(null)}
				onConfirm={handleConfirmDelete}
				title="Delete note?"
				description="This note will be permanently removed for everyone who can see this client."
				confirmText="Delete"
				cancelText="Cancel"
				confirmColor={C.danger}
				isLoading={deleting}
			/>
		</Box>
	);
}
